/*
 * @Author: lizhigang
 * @Date: 2025-12-08 15:20:47
 * @Company: www.smm.cn
 * @Description: 通用hooks
 */
"use client"

import http from "@repo/ui/utils"
import { useLocale, useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"
import { FormInstance, message } from "antd"

import { useMessage } from "./useMessage"

export const useFetchCode = ({
  account,
  form,
  action = "register",
  schema = "modal",
}: {
  account?: string
  form: FormInstance
  action?: "register" | "forgot"
  schema?: "modal" | "page"
}) => {
  const t = useTranslations("register")
  const tForgot = useTranslations("forgot")
  const locale = useLocale()
  const [count, setCount] = useState(60)
  const [sent, setSent] = useState(false)
  const timerRef = useRef<number | null>(null)

  const { showError } = useMessage({ schema })

  /**
   * 验证邮箱是否已注册
   * @param mail 邮箱地址
   */
  const fetchExist = async (mail: string) => {
    try {
      const params = new URLSearchParams({ user_name: mail })
      const response = await http.get<{ user_name: string }, { exist: boolean }>(
        `/usercenter/4.0/check_exist?${params}`,
        {
          showOriginResponse: true,
          getLang: () => locale,
        }
      )
      if (response?.code === 0) {
        return response.data!.exist
      }
      throw new Error(response?.msg)
    } catch (errorInfo) {
      const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
      showError(msg || "unknown error")
    }
  }

  /**
   * 倒计时
   */
  const setCountDown = () => {
    timerRef.current = window.setInterval(() => {
      if (count === 0) {
        clearInterval(timerRef.current as number)
        setCount(60)
      }
      setCount((prevCount) => {
        if (prevCount === 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 60
        }
        return prevCount - 1
      })
    }, 1000) as unknown as number
  }

  /**
   * 验证码查询
   */
  const fetchVerifyCode = async () => {
    try {
      if (!account) {
        return
      }
      setCountDown()
      const formData = new FormData()
      formData.append("code_type", action === "register" ? "register_account" : "reset_account")
      formData.append("email", account.trim())
      formData.append("source", "metal.multi")
      formData.append("language", locale)
      const response = await http.post<FormData, null>(`/usercenter/english/v1/send_email_code`, {
        body: formData,
        showOriginResponse: true,
        getLang: () => locale,
      })
      if (response?.code === 0) {
        setTimeout(() => setSent(true), 1000)
      } else {
        throw new Error(response?.msg)
      }
    } catch (errorInfo) {
      const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
      showError(msg || "unknown error")
    }
  }

  /**
   * 发送验证码
   */
  const fetchCode = async () => {
    if (!account || (count > 0 && count < 60)) return
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(account)) {
      form.setFields([
        {
          name: "user_name",
          errors: [t("validEmail")],
        },
      ])
      return
    }
    const isExist = await fetchExist(account)
    if (action === "forgot" && isExist === true) {
      await fetchVerifyCode()
    }
    if (action === "forgot" && isExist === false) {
      showError(tForgot("email_toast"))
    }

    if (action === "register" && isExist === false) {
      await fetchVerifyCode()
    }
    if (action === "register" && isExist === true) {
      showError(t("alreadyAccount"))
    }
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  return { fetchCode, count, sent }
}
